/**
 * useAuditMap — React Query hook dla Map Audit v2 (G3).
 *
 * Exports:
 *  - useAuditReport(mapId) — query zwraca aktualny audit_report JSONB z thematic_maps
 *  - useAuditMapMutation() — manual "Przelicz audyt" trigger dla auditMapFull
 *  - useFetchPaaBaselineMutation() — manual PAA baseline fetch (użytkownik może
 *     odpalić osobno jeśli chce tylko PAA)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { auditMapFull, auditMapCoverageOnly } from "@/services/validation/mapAuditOrchestrator";
import { fetchPaaBaselineForMap } from "@/services/validation/paaBaselineFetcher";
import type { AuditReportV2 } from "@/types/thematicMaps";

type LogCb = (
  type: "info" | "success" | "error" | "stage",
  message: string,
  debug?: Record<string, unknown>,
) => void;

interface MapAuditRow {
  audit_report: AuditReportV2 | null;
  audit_score: number | null;
  audited_at: string | null;
}

async function fetchAuditReport(mapId: string): Promise<MapAuditRow | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("thematic_maps")
    .select("audit_report, audit_score, audited_at")
    .eq("id", mapId)
    .maybeSingle();
  if (error) throw error;
  return (data as MapAuditRow) || null;
}

export function useAuditReport(mapId: string | null | undefined) {
  return useQuery({
    queryKey: ["audit-report", mapId],
    queryFn: () => (mapId ? fetchAuditReport(mapId) : null),
    enabled: !!mapId,
  });
}

export function useAuditMap(mapId: string | null | undefined) {
  const qc = useQueryClient();

  const invalidate = () => {
    if (!mapId) return;
    qc.invalidateQueries({ queryKey: ["audit-report", mapId] });
    qc.invalidateQueries({ queryKey: ["thematic-maps"] });
  };

  const fullAuditMutation = useMutation({
    mutationFn: async (opts: { skipLLM?: boolean; onLog?: LogCb } = {}) => {
      if (!mapId) throw new Error("mapId required");
      return auditMapFull(mapId, opts);
    },
    onSuccess: invalidate,
  });

  const coverageOnlyMutation = useMutation({
    mutationFn: async (opts: { onLog?: LogCb } = {}) => {
      if (!mapId) throw new Error("mapId required");
      return auditMapCoverageOnly(mapId, opts);
    },
    onSuccess: invalidate,
  });

  const fetchPaaMutation = useMutation({
    mutationFn: async (opts: { onLog?: LogCb; forceRefresh?: boolean } = {}) => {
      if (!mapId) throw new Error("mapId required");
      return fetchPaaBaselineForMap(mapId, opts);
    },
  });

  const auditReport = useAuditReport(mapId);

  return {
    auditReport,
    fullAudit: fullAuditMutation,
    coverageOnly: coverageOnlyMutation,
    fetchPaa: fetchPaaMutation,
  };
}
